import type { PointOfInterest } from "../../@types/mapviewxs";
import type { PreviewCardPosition } from "../../utils/preview-card-position";
import markerComponent from "../map-marker/index.marko";
import { ACTIVE_MARKER_Z_INDEX, MARKER_Z_INDEX } from "./const";

import { extractPosition } from "./helpers";
import type { ItemCardControls, MapAccessibilityTexts, MarkerLibrary, MarkerSelectionState, MarkerWithCircle } from "./types";

interface RenderMarkerOptions {
  map: google.maps.Map;
  markerLibrary: MarkerLibrary;
  points: PointOfInterest[];
  selectionState: MarkerSelectionState;
  isPreviewCardEnabled?: boolean;
  handlePreviewClose?: () => void;
  defaultPreviewPosition?: PreviewCardPosition;
  itemCardControls?: ItemCardControls;
  mapAccessibilityTexts?: MapAccessibilityTexts;
}

export function renderMarker({
  map,
  markerLibrary,
  points,
  selectionState,
  isPreviewCardEnabled,
  handlePreviewClose,
  defaultPreviewPosition,
  itemCardControls,
  mapAccessibilityTexts,
}: RenderMarkerOptions) {
  const position = extractPosition(points[0].geometry);
  if (position.lng === 0) {
    // The backend returns 0 for the longitude of some points
    // which makes the cluster algorithm to not include the marker in a cluster.
    // Altering the latitude for 1 decimal will not change the position of the marker on the map.
    // This is a workaround to avoid that until we have a solution from Google.
    position.lng = 0.000001;
  }

  const { component, node } = markerRenderer(
    points,
    selectionState,
    handlePreviewClose,
    defaultPreviewPosition,
    itemCardControls,
    isPreviewCardEnabled,
    mapAccessibilityTexts,
  );
  const marker = new markerLibrary.AdvancedMarkerElement({
    map,
    position,
    content: node,
    zIndex: selectionState === "selected" ? ACTIVE_MARKER_Z_INDEX : MARKER_Z_INDEX,
  });

  return {
    marker,
    component,
  };
}

function markerRenderer(
  points: PointOfInterest[],
  selectionState: MarkerSelectionState,
  handlePreviewClose?: () => void,
  defaultPreviewPosition?: PreviewCardPosition,
  itemCardControls?: ItemCardControls,
  isPreviewCardEnabled?: boolean,
  mapAccessibilityTexts?: MapAccessibilityTexts,
) {
  const markerEl = markerComponent.renderSync({
    selectionState,
    animationDelay: 0,
    itemCards: points ? points : [],
    itemCardControls,
    onPreviewClose: handlePreviewClose,
    defaultPreviewPosition,
    isPreviewCardEnabled,
    mapAccessibilityTexts,
  });

  // this is needed otherwise marko will complain about the markerEl not being appended to the DOM when updating the component
  const dummy = document.createElement("div");
  markerEl.appendTo(dummy);

  return {
    node: dummy,
    component: markerEl.getComponent(),
  };
}

export function highlightMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    if (point.component.input.selectionState !== "selected") {
      point.component.input.selectionState = "highlighted";
    }
    point.component.forceUpdate();
  }
  return point;
}

export function unHighlightMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    if (point.component.input.selectionState === "highlighted") {
      point.component.input.selectionState = "unselected";
      if (point.circle) {
        point.circle.setMap(null);
        point.circle = undefined;
      }
      point.component.forceUpdate();
    }
  }
  return point;
}

export function unHighlightMarkers(points: MarkerWithCircle[]): MarkerWithCircle[] {
  return points
    .filter(
      (point) => point.circle || point.component?.input?.selectionState === "selected" || point.component?.input?.selectionState === "highlighted",
    )
    .map((point) => unHighlightMarker(point));
}

export function selectMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    point.component.input.selectionState = "selected";
    if ("zIndex" in point.marker) {
      point.marker.zIndex = ACTIVE_MARKER_Z_INDEX;
    }
    point.component.forceUpdate();
  }
  return point;
}

export function unselectMarker(point: MarkerWithCircle): MarkerWithCircle {
  if (point?.component?.input) {
    point.component.input.selectionState = "unselected";
    if ("zIndex" in point.marker) {
      point.marker.zIndex = MARKER_Z_INDEX;
    }
    point.component.forceUpdate();
    if (point.circle) {
      point.circle.setMap(null);
      point.circle = undefined;
    }
  }
  return point;
}

export function unselectMarkers(points: MarkerWithCircle[]): MarkerWithCircle[] {
  return points
    .filter(
      (point) => point.circle || point.component?.input?.selectionState === "selected" || point.component?.input?.selectionState === "highlighted",
    )
    .map((point) => unselectMarker(point));
}
